<script setup lang="ts">
import { useSiteNavigation } from '@corporate/composables'
import type { CorporatePageApiModel } from '@gro-up/api/corporate'
import type { Domain } from '@gro-up/types'
import { DomainContext, SkipToContent } from '@gro-up/ui/components'
import { computed, watch } from 'vue'

import { useAsyncData, useRoute, createError } from '#app'
import { useLogger } from '#imports'
import CorporateLayout from '~/components/Layout/CorporateLayout.vue'
import GrowSubhomePage from '~/components/Page/GrowSubhomePage.vue'
import HomePage from '~/components/Page/HomePage.vue'
import NewsItem from '~/components/Page/NewsItem.vue'

const route = useRoute()
const logger = useLogger()

const requestUrl = computed(() =>
  route.path === '/'
    ? `/api/content/index${route.fullPath.slice(1)}`
    : `/api/content${route.fullPath}`,
)

logger.log('Request URL:', requestUrl.value)

const { data, error } = await useAsyncData<CorporatePageApiModel>(
  () => `content:${route.path}`,
  () => $fetch<CorporatePageApiModel>(requestUrl.value),
)


const siteNavigation = useSiteNavigation()

watch(
  data,
  newData => {
    siteNavigation.value = newData?.siteNavigation ?? undefined
  },
  { immediate: true },
)

// Extract domain from page data for DomainContext (siteNavigation.domain is an object with domain property)
const pageDomain = computed(() => (data.value?.siteNavigation?.domain)?.domain || 'head-office')

if (error.value) {
  console.error('Error fetching page data:', error.value)

  // Handle 404s specifically, otherwise show 500
  const statusCode = error.value?.statusCode === 404 ? 404 : 500

  throw createError({
    statusCode: statusCode,
    message: error.value?.message || 'Page not found',
  })
}

// Also check if data is null/undefined (API returned 404)
if (!data.value) {
  throw createError({
    statusCode: 404,
    message: 'Page not found',
  })
}
</script>

<template>
  <DomainContext :domain="pageDomain as Domain">
    <SkipToContent />

    <CorporateLayout>
      <NewsItem v-if="data && data.type === 'corporateNewsItem'" :page="data" />
      <HomePage v-else-if="data && data.type === 'corporateHome'" :page="data" />
      <GrowSubhomePage v-else-if="data && data.type === 'corporateGrowSubhome'" :page="data" />
      <Page  v-else-if="data" :page="data" />
    </CorporateLayout>
  </DomainContext>
</template>

<style lang="scss" scoped></style>

